import { ClerkLoaded, SignedIn, SignedOut, UserButton, useUser } from '@clerk/clerk-react';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { sortByPopularity } from '../../../collection/components/CollectionCards';
import { CardFooter } from '../../../components/CardFooter';
import { CardHeader } from '../../../components/CardHeader';
import { NavCategory } from '../../../enums/NavCategory.enum';
import { NavCategoryContext } from '../../../enums/NavCategoryContext.enum';
import { SubContext } from '../../../enums/SubContext.enum';
import { useNavCategories } from '../../../hooks/actions/useNavCategories';
import { useCustomer } from '../../../hooks/useCustomer';
import { useProductAssets, Direction } from '../../../hooks/useProductAssets';
import { NavCategorySet } from '../../../models/NavCategorySet.model';
import { ViewProduct } from '../../../models/ViewProduct.model';
import { blankImage } from '../../../product_detail/components/ImageLoader';
import { LazyImageLoader } from '../../../product_detail/components/LazyImageLoader';
import { pageContextState, collectionSelectedVariantFamily } from '../../../recoil/atoms';
import { productListState, getAvailableProducts } from '../../../recoil/selectors';
import CartIcon from '../../../root/components/CartIcon';
import { Link } from '../../../root/components/Link';
import { trackDesktopNavMenuOpened } from '../../../tracking/events';
import { assetPathFn, sizeImage } from '../../../util';
import styles from './DesktopHeader.module.scss';
import { DesktopHeaderNavColumn } from './DesktopHeaderNavColumn';

export const DesktopHeader: React.FC = () => {
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const { navCategorySets } = useNavCategories({ context: NavCategoryContext.DesktopMenu });
  const [activeNavCategorySet, setActiveNavCategorySet] = useState<NavCategorySet>(navCategorySets[0]);
  const pageContext = useRecoilValue(pageContextState);

  const productList = useRecoilValue(productListState);

  const onMouseEnter = (categorySet: NavCategorySet) => {
    setMenuIsOpen(true);
    trackDesktopNavMenuOpened(categorySet.title);
    setHoverCategoryHandleDebounce(categorySet);
  };

  let debounceTimeout: ReturnType<typeof setTimeout>;
  const setHoverCategoryHandleDebounce = (categorySet: NavCategorySet | undefined) => {
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => categorySet && setActiveNavCategorySet(categorySet), 200);
  };

  const onMouseLeave = () => {
    clearTimeout(debounceTimeout);
  };

  return (
    <CardHeader>
      <header role="banner" className={styles.desktopHeader}>
        <div className={styles.desktopHeaderTopNavigation}>
          <div className={styles.left}>
            <Link dataTrackLabel="Our story" dataTrackLocation="Desktop Menu" href="/pages/about-us" fullWidth={false} noMargin={true}>
              Our Story
            </Link>
          </div>
          <div className={styles.center}>
            <Link dataTrackLabel="Logo" dataTrackLocation="header" href="/" buttonType="ghost">
              <img className={styles.hlLogoImage} width="220" src={assetPathFn('honeylove-brandmark-black.svg')} alt="honeylove logo" />
            </Link>
          </div>
          <div className={styles.right}>
            <ClerkLoaded>
              <SignedOut>
                <div className={styles.hoverMenuItemButton}>
                  <Link href={'/pages/loyalty'} dataTrackLabel={'My rewards'} dataTrackLocation={SubContext.DesktopMenu}>
                    <span>My Rewards</span>
                  </Link>
                </div>
              </SignedOut>
              <SignedIn>
                <SignedInNavItem />
              </SignedIn>
            </ClerkLoaded>
            <div>
              <CartIcon />
            </div>
          </div>
        </div>
        <div
          className={styles.desktopHeaderBottomNavigation}
          style={{ maxHeight: menuIsOpen ? '100vw' : 42 }}
          onMouseLeave={() => {
            setMenuIsOpen(false);
          }}
        >
          <div>
            {navCategorySets.map((category) => {
              return (
                <Link
                  key={category.navCategory}
                  dataTrackLabel={category.title}
                  dataTrackLocation={pageContext}
                  href={category.link}
                  onMouseEnter={() => onMouseEnter(category)}
                  onMouseLeave={onMouseLeave}
                  onFocus={() => onMouseEnter(category)}
                  labelStyleOptions={menuIsOpen && category.navCategory === activeNavCategorySet.navCategory ? { color: '#eb4868' } : {}}
                >
                  {category.title === 'All' ? 'Shop' : category.title}
                </Link>
              );
            })}
          </div>
          <div className={styles.shopAllNavigationBar}>
            <CardFooter
              inlineStyles={{
                width: '100%',
                marginBottom: 12,
                marginLeft: -2,
                marginRight: -2,
                boxShadow: `rgba(0, 0, 0, 0.12) 0px 6px 8px`,
              }}
            >
              <div style={{ justifyContent: 'space-between', alignItems: 'flex-start', margin: '30px 60px', display: 'flex', width: '100%' }}>
                {activeNavCategorySet.navCategory === NavCategory.All ? (
                  <DesktopShopFullNavigationBar
                    navCategorySets={navCategorySets.filter((category) => ![NavCategory.All, NavCategory.Bestsellers].includes(category.navCategory))}
                    productList={productList.filter((product) => product.tags.some((c) => activeNavCategorySet.productTags.includes(c)))}
                  />
                ) : (
                  <DesktopShopCategoryNavigationBar
                    category={activeNavCategorySet}
                    productList={productList.filter((product) => product.tags.some((c) => activeNavCategorySet.productTags.includes(c)))}
                  />
                )}
              </div>
            </CardFooter>
          </div>
        </div>
      </header>
    </CardHeader>
  );
};

const DesktopShopFullNavigationBar = ({ productList, navCategorySets }: { productList: ViewProduct[]; navCategorySets: NavCategorySet[] }) => {
  return (
    <>
      {navCategorySets.map((category, index) => {
        const availableProducts = getAvailableProducts(category, [], productList);
        return <DesktopHeaderNavColumn key={`${category.title}-${index}`} availableProducts={availableProducts.sort(sortByPopularity)} category={category} />;
      })}
    </>
  );
};

const DesktopShopCategoryNavigationBar = ({ productList, category }: { productList: ViewProduct[]; category: NavCategorySet }) => {
  const availableProducts = getAvailableProducts(category, [], productList).sort(sortByPopularity);
  const [activeProduct, setActiveProduct] = useState<ViewProduct | undefined>();

  return (
    <div className={styles.shopCategoryNavigationBar}>
      <DesktopHeaderNavColumn
        onActiveProductChange={(product) => {
          setActiveProduct(product);
        }}
        hideProductImage={true}
        key={category.title}
        availableProducts={availableProducts}
        category={category}
        numberOfProducts={5}
        inlineStyle={{ flex: 1, maxWidth: 150, minWidth: 180 }}
      />
      {!activeProduct &&
        availableProducts.slice(0, 3).map((product, i) => {
          return (
            <div key={`${category.title}-${i}`} className={styles.columnImageWrapper} style={{ maxWidth: 279, margin: '12px 24px', flex: 1.25 }}>
              <Link dataTrackLabel="Desktop Navigation" dataTrackLocation="Desktop Navigation" href={`/products/${product.handle}`}>
                <NavColumnProductPreviewImage product={product} />
                <div className={styles.activeTitle}>{product.title}</div>
              </Link>
            </div>
          );
        })}
      {activeProduct && (
        <div className={styles.columnImageWrapper} style={{ maxWidth: 279, margin: '12px 24px', flex: 1.25 }}>
          <Link dataTrackLabel="Desktop Navigation" dataTrackLocation="Desktop Navigation" href={`/products/${activeProduct.handle}`}>
            <NavColumnProductPreviewImage product={activeProduct} showHoverPhoto={activeProduct?.id === activeProduct.id} />
            <div className={styles.activeTitle}>{activeProduct.title}</div>
          </Link>
        </div>
      )}
    </div>
  );
};

const NavColumnProductPreviewImage = ({ product, showHoverPhoto }: { product: ViewProduct; showHoverPhoto?: boolean }) => {
  const photos = useProductAssets(product);
  const [selectedVariant] = useRecoilState(collectionSelectedVariantFamily(product.handle));

  const filteredMannequinPhotos = photos.mannequinPhotos.filter(({ color, direction }) => color === selectedVariant.color && direction === Direction.Front);
  const filteredModelPhotos = photos.modelPhotos.filter(({ color }) => color === selectedVariant.color);
  const mainPhoto = filteredMannequinPhotos[0] ?? filteredModelPhotos[0] ?? { src: blankImage };
  const mainPhotoSrc = sizeImage(mainPhoto.src, '350x443');

  const hoverPhoto = filteredModelPhotos[0] ?? filteredMannequinPhotos[0] ?? { src: blankImage };

  const [isHovering, setIsHovering] = useState<boolean>(false);
  const hoverPhotoSrc = sizeImage(hoverPhoto.src, '350x443');

  const [activeSrc, setActiveSrc] = useState<string>(mainPhotoSrc);
  return (
    <div style={{ display: 'flex' }} onMouseEnter={() => setIsHovering(true)} onFocus={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
      <LazyImageLoader src={isHovering || showHoverPhoto ? hoverPhotoSrc : activeSrc} alt={`shown`} />
    </div>
  );
};

const SignedInNavItem = () => {
  const { user, isLoaded } = useUser();
  const { customer, isFetching } = useCustomer();
  const { totalPoints } = customer;

  if (isFetching) {
    return <></>;
  }

  return (
    <>
      {isLoaded && user && (
        <div className={classnames(styles.hoverMenuItemButton, styles.loggedInButtonWrapper)}>
          <Link href={'/pages/profile'} dataTrackLabel={'My rewards'} dataTrackLocation={SubContext.DesktopMenu}>
            <span>Hi, {user.firstName}</span>
            <div>{totalPoints} Credits</div>
          </Link>
          <UserButton />
        </div>
      )}
    </>
  );
};

export default DesktopHeader;
